import { Box, Button, Stack, Typography } from '@mui/material';
import { createAppScope, permission } from '@nohara-dxdev/oidc-core';
import { OidcProtectedComponent, useScopeIds } from '@nohara-dxdev/react-oidc';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';

import { NumberFieldFormElement } from '@/components';
import { env } from '@/config/env';

import { useSetSettings } from '../api';
import { useSettingValues } from '../hooks';

/** Org-level setting key holding the AI usage limits (MT1-1430). */
export const AI_USAGE_LIMITS_KEY = 'ai-usage-limits';
/** Defaults mirror the backend when no limit has been configured yet. */
export const DEFAULT_AI_MESSAGE_MAX_LENGTH = 400;
export const DEFAULT_AI_MONTHLY_TOKEN_LIMIT = 1_000_000;
const AI_MESSAGE_MAX_LENGTH_CEILING = 32000;

export interface AiUsageLimitsValues {
  messageMaxLength: number;
  monthlyTokenLimit: number;
}

// System-Admin-only (MT1-1430 QA): the requirement is scoped to the APP level
// (`app:{APP_ID}` — no org segment), matching the backend's check. An org App
// Admin's permissions are all prefixed with their org-scoped role scope, so
// they can never satisfy this; only the app-scoped System Admin role can.
const editPermissions = [
  permission({
    subject: 'aiLimits',
    action: 'manage',
    scope: createAppScope(env.APP_ID),
  }),
];

/**
 * MT1-1430: System-Admin-only editor for the org's AI usage limits (max message length +
 * monthly token budget). Gated on `aiLimits.manage` at app scope, so it renders nothing
 * for org-scoped roles — including App Admin. Values are stored on the `ai-usage-limits`
 * org setting; the backend enforces them.
 */
export const AiUsageLimitsForm = () => {
  const { t } = useTranslation();
  const subject = useScopeIds();
  const { getSettingValue } = useSettingValues();
  const { mutate: setSetting, isPending } = useSetSettings();

  const stored = getSettingValue<Partial<AiUsageLimitsValues>>(AI_USAGE_LIMITS_KEY, {});
  const form = useForm<AiUsageLimitsValues>({
    values: {
      messageMaxLength: stored.messageMaxLength ?? DEFAULT_AI_MESSAGE_MAX_LENGTH,
      monthlyTokenLimit: stored.monthlyTokenLimit ?? DEFAULT_AI_MONTHLY_TOKEN_LIMIT,
    },
  });

  const handleSave = (data: AiUsageLimitsValues) => {
    setSetting({ key: AI_USAGE_LIMITS_KEY, value: data });
  };

  return (
    <OidcProtectedComponent requireAll={editPermissions} subject={subject}>
      <FormContainer formContext={form} onSuccess={handleSave}>
        <Stack spacing={2}>
          <Typography variant="h6">{t('settings.aiUsageLimits.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('settings.aiUsageLimits.description')}
          </Typography>
          <NumberFieldFormElement
            name="messageMaxLength"
            label={t('settings.aiUsageLimits.messageMaxLength')}
            min={1}
            max={AI_MESSAGE_MAX_LENGTH_CEILING}
            mode="onChangeCommitted"
            sx={{ maxWidth: 320 }}
          />
          <NumberFieldFormElement
            name="monthlyTokenLimit"
            label={t('settings.aiUsageLimits.monthlyTokenLimit')}
            min={0}
            mode="onChangeCommitted"
            sx={{ maxWidth: 320 }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={isPending}>
              {t('settings.aiUsageLimits.save')}
            </Button>
          </Box>
        </Stack>
      </FormContainer>
    </OidcProtectedComponent>
  );
};
